/* eslint-disable import/prefer-default-export */
import React, { useEffect, useState } from "react";
import { CloseCircle } from "iconsax-react";
import styled from "styled-components";

import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  MAP_COPY,
  MAP_FILTER_CATEGORIES,
  NEAR_ME_FILTER,
} from "../../constants";
import { useMapUi } from "../../hooks/useMapUi";
import {
  getNearbyPois,
  getPoisByCategory,
  isPoiNearbyToUser,
  recordPoiAccess,
} from "../../services/mapService";
import type { MapPoiCategory, MapPoiListItem } from "../../services/map.types";
import { CategoryIcon } from "../../utils/categoryIcons";
import { PoiNearbyTag } from "../PoiNearbyTag";

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 600;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: none;
`;

const Backdrop = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(0, 0, 0, 0.35);
  pointer-events: auto;
  cursor: pointer;
`;

const Sheet = styled.div<{ $bottom: number }>`
  position: relative;
  max-height: 55vh;
  margin-bottom: ${({ $bottom }) => $bottom}px;
  border-radius: 20px 20px 0 0;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.md3Level3};
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Handle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  margin: 10px auto 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CategoryDot = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

const Title = styled.h3`
  flex: 1;
  margin: 0;
  font-size: 19px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const List = styled.div`
  overflow: auto;
  padding: 8px 0 16px;
`;

const ListItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 19px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ItemAddress = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const Loading = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

type FilterCategoryModalProps = {
  visible: boolean;
  category: MapPoiCategory | typeof NEAR_ME_FILTER | null;
  userLatitude?: number;
  userLongitude?: number;
  isResolvingLocation?: boolean;
  bottomOffset?: number;
  onClose: () => void;
  onSelectPoi: (poiId: string) => void;
};

export function FilterCategoryModal({
  visible,
  category,
  userLatitude,
  userLongitude,
  isResolvingLocation = false,
  bottomOffset = 0,
  onClose,
  onSelectPoi,
}: FilterCategoryModalProps) {
  const mapUi = useMapUi();
  const [pois, setPois] = useState<MapPoiListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const isNearMe = category === NEAR_ME_FILTER;
  const hasUserCoords = userLatitude != null && userLongitude != null;

  useEffect(() => {
    if (!visible || !category) return;

    setLoading(true);
    window.setTimeout(() => {
      if (isNearMe) {
        if (!hasUserCoords) {
          setPois([]);
        } else if (userLatitude != null && userLongitude != null) {
          setPois(
            getNearbyPois(userLatitude, userLongitude, MAP_FILTER_CATEGORIES)
          );
        }
      } else {
        setPois(
          getPoisByCategory(
            category as MapPoiCategory,
            userLatitude,
            userLongitude
          )
        );
      }
      setLoading(false);
    }, 0);
  }, [visible, category, isNearMe, hasUserCoords, userLatitude, userLongitude]);

  if (!visible || !category) return null;

  const categoryColor = isNearMe
    ? mapUi.userPin
    : CATEGORY_COLORS[category as MapPoiCategory];

  const showLoading =
    loading || (isNearMe && (isResolvingLocation || !hasUserCoords));

  const handleSelect = (poiId: string) => {
    recordPoiAccess(poiId);
    onSelectPoi(poiId);
    onClose();
  };

  const renderListBody = () => {
    if (showLoading) {
      return <Loading>{MAP_COPY.loading}</Loading>;
    }
    if (pois.length === 0) {
      return <Loading>{MAP_COPY.noResults}</Loading>;
    }
    return pois.map((poi) => {
      const isNearby = isPoiNearbyToUser(poi, userLatitude, userLongitude);
      return (
        <ListItem
          key={poi.id}
          type="button"
          onClick={() => handleSelect(poi.id)}
        >
          <CategoryIcon category={poi.category} size={22} />
          <ItemText>
            <ItemTitle>{poi.title}</ItemTitle>
            {poi.address ? <ItemAddress>{poi.address}</ItemAddress> : null}
          </ItemText>
          {isNearby ? (
            <PoiNearbyTag accentColor={CATEGORY_COLORS[poi.category]} />
          ) : null}
        </ListItem>
      );
    });
  };

  return (
    <Overlay>
      <Backdrop type="button" aria-label={MAP_COPY.cancel} onClick={onClose} />
      <Sheet $bottom={bottomOffset}>
        <Handle />
        <Header>
          <CategoryDot $color={categoryColor}>
            {isNearMe ? (
              <CategoryIcon
                category="saude"
                size={18}
                color="#fff"
                variant="Bold"
              />
            ) : (
              <CategoryIcon
                category={category as MapPoiCategory}
                size={18}
                color="#fff"
                variant="Bold"
              />
            )}
          </CategoryDot>
          <Title>
            {isNearMe
              ? MAP_COPY.nearMe
              : CATEGORY_LABELS[category as MapPoiCategory]}
          </Title>
          <CloseBtn
            type="button"
            onClick={onClose}
            aria-label={MAP_COPY.cancel}
          >
            <CloseCircle size={22} />
          </CloseBtn>
        </Header>
        <List>{renderListBody()}</List>
      </Sheet>
    </Overlay>
  );
}
